import { useCallback, useMemo } from 'react';
import { Building2, PlusCircle, RotateCw, FileSpreadsheet, Tags } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import {
  ODataGrid,
  type ODataGridConfig,
  type GridToolbarHelpers,
} from '@inuxe/prime-odata-grid';
import CommonButton from '../../../components/common-components/button/CommonButton';
import DeleteConfirmationDialog from '../../../components/common-components/pop-up/DeleteConfirmationDialog';
import { useStore } from '../../../hooks/useStore';
import type { IHospitalResult } from '../../../models/hospital/HospitalModel';
import toolbarStyles from '../../../styles/ToolbarButtons.module.css';
import gridStyles from '../../../styles/ODataGridPage.module.css';
import { buildLegacyGridParams, buildODataMappingFromColumns } from '../../../utils/odataGridUtils';
import { getHospitalClosedRowClassName } from '../../../utils/gridRowClassNames';
import NewHospital from '../hospital-files/parts/new-data/new-hospital/NewHospital';
import NewTagNumber from '../hospital-files/parts/new-data/new-tag/NewTagNumber';
import EditHospital from '../hospital-files/parts/edit-data/EditHospital';
import { useHospitalManagementColumns } from './HospitalManagement.grid';
import styles from './HospitalManagement.module.css';
import ExportExcelPopup from './parts/ExportExcelPopup';
import SelectTagsPopup from './parts/SelectTagsPopup';

const HOSPITAL_GRID_ID = 'hospital-management-grid';

const HospitalManagement = () => {
  const { popupStore, hospitalStore } = useStore();
  const columns = useHospitalManagementColumns();

  const oDataMapping = useMemo(
    () => buildODataMappingFromColumns(columns),
    [columns]
  );

  const gridConfig = useMemo<ODataGridConfig<IHospitalResult>>(
    () => ({
      gridId: HOSPITAL_GRID_ID,
      rows: 20,
      oDataMapping,
      fetchRows: async params => {
        const result = await hospitalStore.listAllHospitals(
          buildLegacyGridParams(params, { oDataMapping })
        );
        return {
          rows: result.rows,
          totalRecords: result.lastRow,
        };
      },
    }),
    [hospitalStore, oDataMapping]
  );

  const onGridReady = useCallback(
    (helpers: GridToolbarHelpers<IHospitalResult>) => {
      hospitalStore.setGridHelpers(helpers);
    },
    [hospitalStore]
  );

  const handleNewHospital = () => {
    popupStore.setIsNewHospitalOpen(true);
  };

  const handleNewTagNumber = () => {
    popupStore.setIsNewTagOpen(true);
  };

  const handleExportExcel = () => {
    popupStore.setIsExportExcelOpen(true);
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <span className={styles.titleIcon} aria-hidden="true">
              <Building2 size={24} strokeWidth={2} />
            </span>
            <div className={styles.titleContent}>
              <h2 className={styles.mainTitle}>Hastane Yönetimi</h2>
              <p className={styles.pageDescription}>
                Hastaneleri yönetin, düzenleyin ve etiket numaralarını takip edin.
              </p>
            </div>
          </div>
        </div>
        <div className={styles.gridContainer}>
          <ODataGrid<IHospitalResult>
            gridId={HOSPITAL_GRID_ID}
            config={gridConfig}
            columns={columns}
            dataKey="id"
            wrapperClassName={gridStyles.oDataGridPage}
            resizableColumns
            onGridReady={onGridReady}
            rowClassName={row => getHospitalClosedRowClassName(row.isHospitalClosed)}
            toolbarLeft={helpers => (
              <>
                <CommonButton
                  type="primary"
                  onClick={handleNewHospital}
                  leftIcon={<PlusCircle size={18} strokeWidth={2} />}
                  className={toolbarStyles.toolbarBtnManual}
                >
                  Yeni Hastane Oluştur
                </CommonButton>
                <CommonButton
                  type="outline"
                  onClick={handleNewTagNumber}
                  leftIcon={<Tags size={18} strokeWidth={2} />}
                  className={toolbarStyles.toolbarBtnKn}
                >
                  Hastane Etiket No Ekle
                </CommonButton>
                <CommonButton
                  type="tertiary"
                  onClick={handleExportExcel}
                  leftIcon={<FileSpreadsheet size={18} strokeWidth={2} />}
                  className={toolbarStyles.toolbarBtnExcel}
                >
                  Excel'e Aktar
                </CommonButton>
                <CommonButton
                  type="refresh"
                  onClick={() => helpers.refreshGrid()}
                  leftIcon={<RotateCw size={18} strokeWidth={2} />}
                  className={toolbarStyles.toolbarBtnRefresh}
                >
                  Yenile
                </CommonButton>
              </>
            )}
          />
        </div>
      </div>
      {popupStore.isNewHospitalOpen && <NewHospital />}
      {popupStore.isNewTagOpen && <NewTagNumber />}
      {popupStore.isEditHospitalOpen && <EditHospital />}
      <DeleteConfirmationDialog
        mode="yesNo"
        isOpen={popupStore.isDeleteConfirmationOpen}
        onClose={() => popupStore.setIsDeleteConfirmationOpen(false)}
        onConfirm={async () => {
          if (hospitalStore.selectedHospitalToDelete?.id) {
            await hospitalStore.deleteHospital(hospitalStore.selectedHospitalToDelete.id);
            hospitalStore.refreshGrid();
          }
          popupStore.setIsDeleteConfirmationOpen(false);
        }}
        title="Hastane Silme Onayı"
      />
      {popupStore.isSelectTagsOpen && <SelectTagsPopup />}
      {popupStore.isExportExcelOpen && <ExportExcelPopup />}
    </>
  );
};

export default observer(HospitalManagement);
